import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "./homepage.module.css";
import { useDoLogout } from "src/utils/useDoLogout";
import { useAuth0 } from "@auth0/auth0-react";
import { acceptLatestTerms, checkAcceptedTerms, getLatestTerms } from "src/api/termsAndConditions";
import { useEffect, useState, useCallback } from "react";
import SuspenseLoader from "src/components/SuspenseLoader/SuspenseLoader";
import ReactMarkdown from "react-markdown";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import CheckNotifications from "src/components/Notifications/CheckNotifications";
import { updateEmailConsent, getEmailConsent } from "src/api/emailConsent";
import { isAndroid, isIOS } from "src/utils/platformUtils";
import CheckTermsAndCondition from "src/components/Notifications/CheckTermsAndCondition";

function TermsAndConditionsPage() {
  const navigate = useNavigate();
  // Use function doLogOut from custom hook useDoLogout
  const doLogOut = useDoLogout();
  const { isLoading, isAuthenticated } = useAuth0();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const [termsText, setTermsText] = useState("");
  const [isChecking, setIsChecking] = useState(true);
  const [emailOptIn, setEmailOptIn] = useState(false);
  // Terms checkbox accepted state
  const [termsCheckboxAccepted, setTermsCheckboxAccepted] = useState(false);

  const checkAcceptAndFetchTerms = useCallback(async () => {
    // Set our own loading state to true at the start of the check
    setIsChecking(true);
    try {
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();

      if (!accessTokenFromAuth0) {
        // Logout is in progress, stop execution.
        setIsChecking(false);
        return;
      }

      // CRITICAL FIX 1: Check for acceptance BEFORE doing anything else.
      const accepted = await checkAcceptedTerms(accessTokenFromAuth0);
      //    if (accepted && latestTerms.id) {
      if (accepted) {
        // If user already accepted, they should not be here.
        // Navigate away and stop execution immediately.
        navigate("/home", { replace: true });
        return; // This prevents the call to getLatestTerms.
      }

      // This code will only run if terms were NOT accepted.
      const latestTerms = await getLatestTerms(accessTokenFromAuth0);
      setTermsText(latestTerms.text);

      // Load current email consent so checkbox reflects MongoDB value
      try {
        const consent = await getEmailConsent(accessTokenFromAuth0);
        setEmailOptIn(consent.emailNotificationsOptIn === true);
      } catch {
        // Leave default false if fetch fails
      }
    } catch (error) {
      console.log("Error fetching terms details:", error);
      // Account was deleted elsewhere / session rejected → logout → /get-started (avoids black screen)
      const status = (error as { status?: number })?.status;
      if (status === 404 || status === 401) {
        await doLogOut();
        return;
      }
    } finally {
      // Set our own loading state to false when the check is complete.
      setIsChecking(false);
    }
  }, [getSafeAccessAuth0Token, navigate, doLogOut]);

  useEffect(() => {
    if (isLoading) {
      return; // Wait for Auth0 SDK to be ready
    }

    if (isAuthenticated) {
      checkAcceptAndFetchTerms();
    } else {
      // If user is not authenticated, kick off the login flow.
      navigate("/", { replace: true });
    }
  }, [isLoading, isAuthenticated, checkAcceptAndFetchTerms, navigate]);

  const handleAgreeTerms = async () => {
    try {
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();

      if (!accessTokenFromAuth0) {
        return; // Logout is in progress.
      }

      await acceptLatestTerms(accessTokenFromAuth0);

      // Always persist current checkbox state: checked = opt-in (true), unchecked = opt-out (false, sets emailUnsubscribedAt)
      updateEmailConsent(accessTokenFromAuth0, emailOptIn, "terms_and_conditions").catch((err) =>
        console.error("Failed to save email consent:", err)
      );

      // After accept terms and conditions, redirect to /home and render app
      navigate("/home", { replace: true });
    } catch (error) {
      console.error("Something went wrong when user agree terms", error);
      // throw error;
    }
  };

  const handleDisagreeTerms = async () => {
    await doLogOut();
  };

  // CRITICAL FIX 2: Show loader if Auth0 is loading OR if we are checking.
  // This will cover the network latency window in production.
  if (isLoading || isChecking) {
    return <SuspenseLoader />;
  }

  if (!termsText) {
    // This can happen if the API call finishes but there's no text.
    // We render nothing instead of an empty component.
    return null;
  }

  const isNative = isIOS || isAndroid;

  return (
    <Box className={`${styles.termsContainer} ${styles.termsPageRoot}`} sx={{
      maxHeight: "none",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      padding: 2,
      ...(isNative && {
        paddingBottom: "calc(20px + max(env(safe-area-inset-bottom), 15px))",
      }),
    }}>
      <Box className={styles.TermsBoxContainer} sx={{ flex: 1, minHeight: 0 }}>
        <Typography variant="h4" textAlign="center" fontSize="23px">
          Terms and Conditions
        </Typography>
        {/* Terms and conditions text */}
        <Typography variant="body1" fontSize="16px" p={0}>
          <ReactMarkdown className={styles.TermsReactMarkdown}>{termsText}</ReactMarkdown>
        </Typography>

      </Box>

      <Box display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" sx={{ alignSelf: "flex-start" }}>
        {/* Notifications consent checkbox */}
        <CheckNotifications checked={emailOptIn} onChange={setEmailOptIn} />
        {/* Terms and conditions checkbox — must be checked to enable Next */}
        <CheckTermsAndCondition checked={termsCheckboxAccepted} onChange={setTermsCheckboxAccepted} />
      </Box>


      {/* Terms and conditions buttons */}
      <Box className={styles.TermsButtonsContainer} sx={{ flexShrink: 0 }} pt={2}>
        <Button
          variant="contained"
          onClick={handleDisagreeTerms}
          className={styles.TermsButtons}
          sx={{
            width: "120px",
            p: 0.5,
            color: "black",
            backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
            "&:hover": {
              color: "black",
              backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAgreeTerms}
          disabled={!termsCheckboxAccepted}
          sx={{
            width: "120px", p: 0.5,
            "&:disabled": {
              backgroundColor: PureLightThemeCaaS.colors.alpha.white[30],
              color: PureLightThemeCaaS.colors.alpha.white[50],
            },
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default TermsAndConditionsPage;
